import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { CommonModule, isPlatformBrowser } from '@angular/common';

import { CardValidationResponse, PaymentSession, PaymentStateEvents } from '../../store/payment.state';
import { selectPaymentSession, selectPaymentStateEvents } from '../../store/payment.selector';
import { cardValidationError } from '../../constant/payment-constant';
import { clearTokenResponse, setCreditCardTokenResponse, stopCreditCardLoading } from '../../store/payment.action';
import { environment } from '../../../environments/environment';
import { CardSkeletalComponent } from '../card-skeletal/card-skeletal.component';

declare const SafeCharge: any;

@Component({
  selector: 'app-credit-card-payment',
  imports: [CommonModule, CardSkeletalComponent],
  templateUrl: './credit-card-payment.component.html',
})

export class CreditCardPaymentComponent {
  sfc: any;
  scard: any;
  cardNumber: any;
  cardExpiry: any;
  cardCVV: any;

  validCardNumber = false;
  validExpiry = false;
  validCVV = false;
  isAllFieldValid = false;
  isCardNumberReady = false;
  isExpiryReady = false;
  isCVVReady = false;
  cardValidationResponses: CardValidationResponse[] = [];
  cardNameError = "";
  cardNumberError = "";
  cardExpiryError = "";
  cardCVVError = "";
  isTokenGot = false;
  isLoading = false;
  paymentSession: PaymentSession | undefined;

  selectPaymentSession$: any;
  selectPaymentStateEvents$: any;

  private subscriptions$ = new Subscription();
  readonly cardValidationError = cardValidationError;

  constructor(private store: Store, @Inject(PLATFORM_ID) private platformId: object) {
    this.selectPaymentSession$ = this.store.select(selectPaymentSession);
    this.selectPaymentStateEvents$ = this.store.select(selectPaymentStateEvents);
  }

  ngOnInit(): void {
    this.subscriptions$.add(this.selectPaymentStateEvents$.subscribe((data: PaymentStateEvents) => this.bindselectPaymentStateEvents(data)));
    this.subscriptions$.add(this.selectPaymentSession$.subscribe((data: PaymentSession) => this.bindSelectPaymentSession(data)));
  }

  getSessionToken() {
    return this.paymentSession?.sessionToken ?? '';
  }

  bindSelectPaymentSession(data: PaymentSession) {
    if (data.sessionToken != null && (this.paymentSession?.sessionToken !== data.sessionToken)) {
      this.paymentSession = data;
      if (this.paymentSession?.sessionToken !== '') {
        this.initSDK(data);
      }
      else {
        this.store.dispatch(stopCreditCardLoading());
      }
    }
  }

  bindselectPaymentStateEvents(paymentStateEvents: PaymentStateEvents) {
    if (paymentStateEvents.creditCardLoading) {
      this.isLoading = true;
    }
    else {
      this.isLoading = false;
    }
  }

  initSDK(paymentSession: PaymentSession) {
    if (isPlatformBrowser(this.platformId)) {
      const placeholder = document.querySelector('.webSDK-placeholder');
      const toggleContent = document.querySelector('.toggle-content');

      if (placeholder) this.hideElement(placeholder);
      if (toggleContent) this.showElement(toggleContent);

      var sfc = SafeCharge({
        merchantId: paymentSession.merchantId,
        merchantSiteId: paymentSession.merchantSiteId,
        sessionToken: paymentSession.sessionToken,
        env: environment.application.nuveiAPIEnvironment
      });
      this.sfc = sfc;
      var fieldStyles = this.getFieldStyles();
      this.setFieldStyles(sfc, fieldStyles);
    }

  }

  private setFieldStyles(sfc: any,
    fieldStyles: {
      base: {
        color: string; fontSize: string; fontSmoothing:
        string; '::placeholder': { color: string; };
      }; invalid: { color: string; ':focus': { color: string; }; };
      empty: { color: string; '::placeholder': { color: string; }; }; valid: { color: string; };
    }) {
    this.destroyFields();
    var fieldClasses = {
      focus: 'focused',
      empty: 'empty',
      invalid: 'invalid',
    };

    let ScFields = sfc.fields({
      locale: "en"
    });
    let cardNumber = ScFields.create('ccNumber', {
      style: fieldStyles,
      classes: fieldClasses,
    });
    cardNumber.attach('#card-number');
    this.cardNumber = cardNumber;
    let cardExpiry = ScFields.create('ccExpiration', {
      style: fieldStyles,
      classes: fieldClasses,
    });

    cardExpiry.attach('#card-expiry');
    this.cardExpiry = cardExpiry;
    let cardCvc = ScFields.create('ccCvc', {
      style: fieldStyles,
      classes: fieldClasses,
    });
    cardCvc.attach('#card-cvc');
    this.cardCVV = cardCvc;

    this.fieldsOnChange();
    this.fieldsReady();
    this.fieldsOnBlur();
  }

  private destroyFields() {
    if (this.cardNumber) {
      this.cardNumber.destroy();
      this.cardNumberError = "";
    }
    if (this.cardExpiry) {
      this.cardExpiry.destroy();
      this.cardExpiryError = "";
    }
    if (this.cardCVV) {
      this.cardCVV.destroy();
      this.cardCVVError = "";
    }
    const cardHolderNameInput = document.getElementById('cardHolderName') as HTMLInputElement;
    cardHolderNameInput!.value = '';
    this.cardValidationResponses = [];
  }

  private handleCardFieldChange(cardField: any, fieldName: string) {
    cardField.on('change', (cardValidationResponse: CardValidationResponse) => {
      this.removeAndAddNewResponse(cardValidationResponse, fieldName);
      const isValid = cardValidationResponse.complete;

      switch (fieldName) {
        case "ccNumber":
          this.validCardNumber = isValid;
          break;
        case "ccExpiration":
          this.validExpiry = isValid;
          break;
        case "ccCvc":
          this.validCVV = isValid;
          break;
      }

      this.validationCheck();
    });
  }

  private fieldsOnChange() {
    this.handleCardFieldChange(this.cardNumber, "ccNumber");
    this.handleCardFieldChange(this.cardExpiry, "ccExpiration");
    this.handleCardFieldChange(this.cardCVV, "ccCvc");
  }

  private fieldsOnBlur() {
    const fields = [
      { field: this.cardNumber, id: "ccNumber" },
      { field: this.cardExpiry, id: "ccExpiration" },
      { field: this.cardCVV, id: "ccCvc" },
    ];

    fields.forEach(fieldInfo => {
      fieldInfo.field.on('blur', (event: any) => {
        const response = this.cardValidationResponses.find(x => x.field === fieldInfo.id);
        if (response) {
          if (fieldInfo.id === "ccNumber") {
            this.cardNumberError = !response.complete ? this.getErrorMessage(response?.error?.id) : "";
          }
          if (fieldInfo.id === "ccExpiration") {
            this.cardExpiryError = !response.complete ? this.getErrorMessage(response?.error?.id) : "";
          }
          if (fieldInfo.id === "ccCvc") {
            this.cardCVVError = !response.complete ? this.getErrorMessage(response?.error?.id) : "";
          }
        }
      });
    });
  }

  private getErrorMessage(errorCode: string) {
    if (cardValidationError.hasOwnProperty(errorCode)) {
      return cardValidationError[errorCode].errorMessage;
    } else {
      return "";
    }
  }

  private removeAndAddNewResponse(cardValidationResponse: CardValidationResponse, field: string) {
    let indexOfItemToRemove = this.cardValidationResponses.findIndex(item => item.field === field);

    if (indexOfItemToRemove !== -1) {
      this.cardValidationResponses.splice(indexOfItemToRemove, 1);
    }

    this.cardValidationResponses.push(cardValidationResponse);
  }

  private fieldsReady() {
    this.cardNumber.on('ready', (cardValidationResponse: any) => {
      this.isCardNumberReady = true;
      this.checkIsFieldsReady();
    });

    this.cardExpiry.on('ready', (cardValidationResponse: any) => {
      this.isExpiryReady = true;
      this.checkIsFieldsReady();
    });

    this.cardCVV.on('ready', (cardValidationResponse: any) => {
      this.isCVVReady = true;
      this.checkIsFieldsReady();
    });
  }

  private getFieldStyles() {
    return {
      base: {
        color: '#4A5568',
        fontSize: "14px",
        fontSmoothing: 'antialiased',
        '::placeholder': {
          color: '#95AAC1'
        }
      },
      invalid: {
        color: '#E30045',
        ':focus': {
          color: '#4A5568'
        }
      },
      empty: {
        color: '#4A5568',
        '::placeholder': {
          color: '#95AAC1'
        }
      },
      valid: {
        color: '#1AE5BE'
      }
    };
  }

  validationCheck() {
    if (this.validCVV && this.validCardNumber && this.validExpiry && this.getCardHolderName()) {
      this.getCreditCardToken();
    }

    if (this.isTokenGot && (!this.validCVV || !this.validCardNumber || !this.validExpiry)) {
      this.store.dispatch(clearTokenResponse());
    }
  }

  checkIsFieldsReady() {
    if (this.isCardNumberReady && this.isCVVReady && this.isExpiryReady) {
      this.store.dispatch(stopCreditCardLoading());
    }
  }

  showCardAlert() {
    const fields = [
      { field: this.cardNumber, id: "ccNumber" },
      { field: this.cardExpiry, id: "ccExpiration" },
      { field: this.cardCVV, id: "ccCvc" }
    ];

    fields.forEach(fieldInfo => {
      const response = this.cardValidationResponses.find(x => x.field === fieldInfo.id);
      if (!response || !response.complete) {
        if (fieldInfo.id === "ccNumber") {
          this.cardNumberError = this.cardNumberError ? this.cardNumberError : "Please enter card number";
        }
        if (fieldInfo.id === "ccExpiration") {
          this.cardExpiryError = this.cardExpiryError ? this.cardExpiryError : "Please enter card expiry";
        }
        if (fieldInfo.id === "ccCvc") {
          this.cardCVVError = this.cardCVVError ? this.cardCVVError : "Please enter card cvv";
        }
      }
    });

    if (!this.getCardHolderName()) {
      this.cardNameError = "Please enter card holder name"
    }
  }

  getCardHolderName() {
    return (<HTMLInputElement>document.getElementById('cardHolderName')).value
  }

  onInputNameChange(event: any) {
    if (event.target.value) {
      this.cardNameError = "";
    }
    else {
      this.cardNameError = "Please enter card holder name";
    }
  }

  getToken() {
    if (this.validCVV && this.validCardNumber && this.validExpiry && this.getCardHolderName()) {
      this.getCreditCardToken();
      return true;
    }
    else {
      this.showCardAlert();
      return false;
    }
  }

  getCreditCardToken() {
    this.sfc.getToken(this.cardNumber, {
      sessionToken: this.paymentSession?.sessionToken,
      cardHolderName: (<HTMLInputElement>document.getElementById('cardHolderName')).value
    }).then((result: any) => {
      if (result.status === "SUCCESS")
        this.isTokenGot = true;
      this.store.dispatch(setCreditCardTokenResponse({ result: result }));
    });
  }

  hideElement(elem: Element) {
    elem.classList.add('is-hide');
  }

  showElement(elem: Element) {
    elem.classList.add('is-visible');
  }

  ngOnDestroy() {
    this.subscriptions$.unsubscribe();
    //this.store.dispatch(clearPaymentSession());
  }

}





